import {Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {MatChipInputEvent} from '@angular/material/chips';

@Component({
    selector: 'emm-add-component-dialog',
    templateUrl: './add-component-dialog.component.html',
    styleUrl: './add-component-dialog.component.scss',
    standalone: false
})
export class AddComponentDialogComponent implements OnInit{

    private readonly COMPONENT_CONTROL_NAME = 'newComponent';

    form: FormGroup;
    allowedComponents = [
        {
            name: 'Lite Ice',
            tag: 'ICE_LITE',
        },
        {
            name: 'Extra Ice',
            tag: 'ICE_EXTRA',
        },
        {
            name: 'No Ice',
            tag: 'ICE_NONE',
        }
    ];
    selectedComponents = [];
    existingComponents: string[] = [];
    readonly separatorKeysCodes: number[] = [ENTER, COMMA];

    @ViewChild('optionInput') optionInput!: ElementRef<HTMLInputElement>;

    get newComponentControl(): FormControl {
        return this.form.get(this.COMPONENT_CONTROL_NAME) as FormControl;
    }

    constructor(
        public dialogRef: MatDialogRef<AddComponentDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { existingComponents?: any[] }
    ) {}

    ngOnInit() {
        // TODO: Add logic that only allows relevant components to be added.
        //  This should be based on the class of the item that opened this dialog and we may need an autocomplete
        //  rather than a text field.

        if (this.data?.existingComponents) {
            this.existingComponents = this.data.existingComponents
                .map(c => typeof c === 'string' ? c.trim() : c?.tag)
                .filter(Boolean)
                .map(tag => this.normalizeTag(tag));
        }

        this.buildForm();
    }

    buildForm() {
        this.form = new FormGroup({
            [this.COMPONENT_CONTROL_NAME]: new FormControl('', [Validators.required, this.duplicateComponentValidator()])
        });
    }

    private normalizeTag(tag: string): string {
        return tag.trim().toUpperCase();
    }

    duplicateComponentValidator(): ValidatorFn {
        return (control: AbstractControl): Record<string, any> | null => {
            const inputValue = control.value?.trim();
            if (!inputValue) {
                return null;
            }

            const newTags = inputValue
                .split(',')
                .map(tag => this.normalizeTag(tag))
                .filter(Boolean);

            const duplicates = newTags.filter(tag => this.existingComponents.includes(tag));

            return duplicates.length > 0
                ? { duplicateComponent: { value: duplicates.join(', ') } }
                : null;
        };
    }

    close(): void {
        this.dialogRef.close();
    }

    save() {
        if (this.form.valid) {
            this.dialogRef.close(this.newComponentControl.value);
            // this.dialogRef.close(this.selectedComponents);
        }
    }

    onEnterKey(event: KeyboardEvent) {
        event.preventDefault();

        if (this.form.valid && !this.form.pristine) {
            this.save();
        }
    }

    add(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();

        if (value) {
            this.addComponent(value);
        }

        if (event.input) {
            event.input.value = '';
        }
        this.clearFormControl();
    }

    remove(option: string): void {
        const index = this.selectedComponents.indexOf(option);
        if (index >= 0) {
            this.selectedComponents.splice(index, 1);
        }
    }

    selected(event: MatAutocompleteSelectedEvent): void {
        this.addComponent(event.option.viewValue);
        this.optionInput.nativeElement.value = '';
        this.clearFormControl();
    }

    private addComponent(value: string): void {
        if (value && !this.selectedComponents.includes(value)) {
            this.selectedComponents.push(value);
        }
    }

    private clearFormControl(): void {
        this.newComponentControl.setValue(null);
    }
}
